import { useEffect, useState } from 'react';
import { getNotificationPrefs, updateNotificationPrefs, type NotificationPrefs } from '@lib/api/portal';

export const NotificationSettingsPage = () => {
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getNotificationPrefs().then(setPrefs).catch(() => setError("Failed to load notification settings."));
  }, []);

  const toggle = (key: keyof NotificationPrefs) => {
    if (!prefs) return;
    setPrefs({ ...prefs, [key]: !prefs[key] });
    setSaved(false);
  };

  const save = async () => {
    if (!prefs) return;
    setBusy(true);
    try {
      await updateNotificationPrefs(prefs);
      setSaved(true);
      setError(null);
    } catch {
      setError('Could not save settings — please try again.');
    } finally { setBusy(false); }
  };

  if (error && !prefs) return <div className="px-4 py-6 text-sm text-red-600 flex items-center gap-2 bg-red-50 rounded-xl border border-red-200"><span className="material-symbols-outlined text-base">error</span>{error}</div>;
  if (!prefs) return <div className="h-24 animate-pulse rounded-xl bg-gray-100" />;

  const Row = ({ label, desc, k }: { label: string; desc: string; k: keyof NotificationPrefs }) => (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
      <div>
        <p className="text-sm font-medium text-gray-900">{label}</p>
        <p className="text-xs text-gray-500">{desc}</p>
      </div>
      <button
        onClick={() => toggle(k)}
        className={`relative inline-flex h-6 w-11 rounded-full transition-colors ${prefs[k] ? 'bg-[#C77D3F]' : 'bg-gray-300'}`}
      >
        <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform mt-0.5 ${prefs[k] ? 'translate-x-5' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold text-[#2C1810]">Notification Settings</h1>
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <Row label="Email notifications" desc="Receive emails for schedule changes" k="email_enabled" />
        <Row label="SMS notifications" desc="Receive text messages (requires phone on file)" k="sms_enabled" />
        <Row label="Hours reminder" desc="Reminders to log hours at 4:30pm and 6pm" k="hours_reminder_enabled" />
        <Row label="Schedule updates" desc="Alerts when your work schedule changes" k="schedule_notifications" />
        <Row label="New documents" desc="Alerts when documents are shared with you" k="document_notifications" />
      </div>
      {saved && <p className="text-sm text-green-700">Settings saved.</p>}
      {error && prefs && <p className="text-sm text-red-600">{error}</p>}
      <button
        onClick={save}
        disabled={busy}
        className="bg-[#C77D3F] text-white rounded-lg px-6 py-2.5 text-sm font-semibold hover:bg-[#b36e35] disabled:opacity-50"
      >
        {busy ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
};
